import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { network } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { DollarSign, TrendingUp, Repeat, ArrowLeft, Copy, Check } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function EarningsPage() {
  const [data, setData]             = useState<any>(null);
  const [partnerships, setPartnerships] = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [copied, setCopied]         = useState<string | null>(null);

  useEffect(() => {
    Promise.all([network.earnings(), network.partnerships()])
      .then(([e, p]) => { setData(e); setPartnerships(p.partnerships); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const copySlug = (slug: string) => {
    const url = `${window.location.origin}/api/public/convert/${slug}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(slug);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <Spinner className="h-screen" />;

  const totals    = data?.totals;
  const chartData = (data?.monthly ?? []).slice().reverse().map((m: any) => ({
    month: m.month, Earned: parseFloat(m.earned) || 0,
  }));

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
        <Link to="/network" className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">Earnings</h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">Commission earnings from your partnerships</p>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { icon: DollarSign, label: "Total earned",     value: `$${parseFloat(totals?.total_earned || 0).toFixed(2)}`, accent: "bg-green-50 text-green-600" },
            { icon: Repeat,     label: "Conversions",      value: totals?.total_conversions || 0,                         accent: "bg-coral/10 text-coral"    },
            { icon: TrendingUp, label: "Active partners",  value: partnerships.filter((p) => p.status === "active").length, accent: "bg-blue-50 text-blue-600"},
          ].map(({ icon: Icon, label, value, accent }) => (
            <div key={label} className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <div className={`p-2.5 rounded-xl w-fit mb-3 ${accent}`}><Icon size={16} /></div>
              <p className="text-xl sm:text-2xl font-bold text-ink leading-none">{value}</p>
              <p className="text-xs text-slate mt-1.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Monthly earnings chart */}
        {chartData.length > 0 && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
            <h2 className="font-bold text-ink mb-5">Monthly earnings</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 0, right: 4, bottom: 0, left: -20 }}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5A6872" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#5A6872" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`, "Earned"]}
                  contentStyle={{ background: "#0B1215", border: "none", borderRadius: 10, color: "white", fontSize: 12 }} />
                <Bar dataKey="Earned" fill="#FF5F45" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Partnerships */}
        {partnerships.length > 0 ? (
          <div>
            <h2 className="font-bold text-ink mb-3">My partnerships</h2>
            <div className="space-y-3">
              {partnerships.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink truncate">{p.offer_title}</p>
                      <p className="text-xs text-slate mt-0.5">
                        by {p.merchant_name} · {p.commission_pct}% commission · {p.conversion_count} conversions
                      </p>
                    </div>
                    <p className="text-lg font-bold text-green-600 flex-shrink-0">${parseFloat(p.earned || 0).toFixed(2)}</p>
                  </div>
                  {/* Tracking link */}
                  <div className="flex items-center gap-2 bg-black/[0.02] rounded-xl px-3 py-2.5">
                    <code className="text-xs text-slate flex-1 truncate">
                      {`${typeof window !== "undefined" ? window.location.origin : ""}/api/public/convert/${p.tracking_slug}`}
                    </code>
                    <button onClick={() => copySlug(p.tracking_slug)}
                      className="flex-shrink-0 p-1.5 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04] transition-colors">
                      {copied === p.tracking_slug
                        ? <Check size={13} className="text-green-600" />
                        : <Copy size={13} />
                      }
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-14 text-center">
            <DollarSign size={32} className="text-slate opacity-20 mx-auto mb-4" />
            <p className="font-semibold text-ink mb-1">No earnings yet</p>
            <p className="text-sm text-slate mb-5">Join offers in the marketplace to start earning commissions.</p>
            <Link to="/network"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              Browse marketplace
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
